import PropTypes from 'prop-types';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import React, { useState, useCallback, useEffect } from 'react';

import { useUserApi } from '@/shared/services/userApi';
import welcomeImg from '@/assets/img/onboarding/colorfulColoradoAustinSullivan.jpg';
import biliImg from '@/assets/img/icons/svg/babyBili.svg';
import spotlightsImg from '@/assets/img/spotlights/mainImage.avif';
import dataSourcesImg from '@/assets/img/spotlights/coloradoRiver.jpg';
import dataProductsImg from '@/assets/img/spotlights/storymap-preview.jpg';
import communitiesImg from '@/assets/img/icons/svg/peopleIcon.svg';
import dashboardImg from '@/assets/img/carousel/rockyMountainPeterPryharski.jpg';
import profileImg from '@/assets/img/icons/svg/profileIcon.svg';
import finishImg from '@/assets/img/carousel/mountainLakeBenjCollins.jpg';

import './OnboardingTour.css';

// Each slide carries a per-slide image for visual variety. `imageContain` marks
// icon-style art (rendered "contained" on a light backdrop) versus photographs
// (rendered full-bleed with object-fit: cover). Every image has meaningful alt
// text for screen readers (WCAG 2.1 AA).
const TOUR_STEPS = [
    {
        title: 'Welcome to the Sustainability Hub!',
        body: 'We are glad you are here. This quick tour highlights the key features of the platform. You can skip at any time and revisit it later from your dashboard.',
        image: welcomeImg,
        imageAlt: 'A colorful Colorado landscape at sunset',
    },
    {
        title: 'Meet Bili',
        body: 'Bili is your AI sustainability assistant. Ask about environmental topics, get personalized tips, and explore Colorado sustainability data.',
        image: biliImg,
        imageAlt: "Bili, the Sustainability Hub's friendly assistant mascot",
        imageContain: true,
        link: '/bili-chat',
        linkLabel: 'Try Bili Chat',
    },
    {
        title: 'Explore Spotlights',
        body: 'Spotlights are deep dives into key topics like water, energy, and wildfires, each combining real data sources, interactive tools, and expert analysis.',
        image: spotlightsImg,
        imageAlt: 'A preview of Sustainability Hub spotlight topics',
        link: '/spotlights',
        linkLabel: 'Browse Spotlights',
    },
    {
        title: 'Data Sources',
        body: 'Discover real-time environmental data from agencies like USGS, NOAA, and the Colorado Division of Water Resources. Bili can help you explore it.',
        image: dataSourcesImg,
        imageAlt:
            'The Colorado River, a source of real-time environmental data',
        link: '/data?tab=sources',
        linkLabel: 'Browse Data Sources',
    },
    {
        title: 'Data Products',
        body: 'Explore interactive StoryMaps, dashboards, and reports that visualize sustainability trends across Colorado.',
        image: dataProductsImg,
        imageAlt: 'Preview of an interactive sustainability StoryMap',
        link: '/data?tab=products',
        linkLabel: 'Browse Data Products',
    },
    {
        title: 'Communities of Interest',
        body: 'Join mini forums focused on specific sustainability topics. Collaborate with others, share resources, and work together on solutions.',
        image: communitiesImg,
        imageAlt: 'An icon of people representing communities of interest',
        imageContain: true,
        link: '/communities',
        linkLabel: 'Browse Communities',
    },
    {
        // No link button here: the tour launches from the dashboard, so a
        // "Go to Dashboard" button would point at the page the user is already
        // on — redundant with Next and the original source of the resume
        // dead-end (spike #980, item 2). The slide just describes the dashboard.
        title: 'Your Dashboard',
        body: 'Your dashboard is your home base. Access your conversations with Bili, view Sustainability Spotlights, and explore other platform features.',
        image: dashboardImg,
        imageAlt: 'Rocky Mountain peaks in Colorado',
    },
    {
        title: 'Your Profile',
        body: 'Complete your profile to personalize your experience. Add your interests, bio, and organization so Bili and the community can better serve you.',
        image: profileImg,
        imageAlt: 'A user profile icon',
        imageContain: true,
        link: '/profile',
        linkLabel: 'View Profile',
    },
    {
        title: 'You are all set!',
        body: 'You are ready to explore the Sustainability Hub. Bili is always available in the bottom-right corner if you need help. Happy exploring!',
        image: finishImg,
        imageAlt: 'A calm mountain lake in Colorado',
    },
];

export const TOUR_STEP_COUNT = TOUR_STEPS.length;

export default function OnboardingTour({
    show,
    onComplete,
    onPause,
    initialStep = 0,
}) {
    const [step, setStep] = useState(initialStep);
    const { markTourComplete, saveTourStep } = useUserApi();
    const { pathname } = useLocation();

    // Sync step when the tour is (re)opened with a different initialStep
    useEffect(() => {
        if (show) {
            setStep(initialStep);
        }
    }, [show, initialStep]);

    const current = TOUR_STEPS[step];
    const isFirst = step === 0;
    const isLast = step === TOUR_STEPS.length - 1;
    const progress = Math.round(((step + 1) / TOUR_STEPS.length) * 100);

    const handleNext = useCallback(async () => {
        if (isLast) {
            try {
                await markTourComplete();
            } catch {
                // Best-effort; still dismiss the modal
            }
            onComplete();
        } else {
            const nextStep = step + 1;
            setStep(nextStep);
            saveTourStep(nextStep).catch(() => {});
        }
    }, [step, isLast, onComplete, markTourComplete, saveTourStep]);

    const handlePrev = useCallback(() => {
        if (!isFirst) {
            setStep(step - 1);
        }
    }, [step, isFirst]);

    const handleSkip = useCallback(async () => {
        try {
            await markTourComplete();
        } catch {
            // Best-effort; still dismiss the modal
        }
        onComplete();
    }, [onComplete, markTourComplete]);

    const handleLinkClick = useCallback(() => {
        // Save the next step so the user can resume after visiting the feature
        const nextStep = step + 1;
        saveTourStep(nextStep).catch(() => {});

        // Defensive guard: if a slide ever links to the page it is shown on,
        // pausing would dead-end (no route change to remount the page, and
        // ContinueTourBanner hides on that page). Advance the modal in place
        // instead. No shipped slide triggers this today — the degenerate
        // "Go to Dashboard" button was removed — but it keeps the whole class
        // of resume dead-ends from ever recurring (spike #980, item 2).
        if (current?.link === pathname) {
            setStep(nextStep);
            return;
        }

        // Otherwise pause (not complete) so ContinueTourBanner shows on the
        // target page and the user can resume from there.
        if (onPause) {
            onPause(nextStep);
        } else {
            onComplete();
        }
    }, [step, current, pathname, onComplete, onPause, saveTourStep]);

    if (!current) return null;

    return (
        <Modal
            show={show}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            aria-labelledby="onboarding-tour-title"
        >
            <Modal.Body className="text-center py-4 px-4">
                {current.image && (
                    <img
                        src={current.image}
                        alt={current.imageAlt || ''}
                        className={`onboarding-tour-hero w-100 rounded mb-3${
                            current.imageContain
                                ? ' onboarding-tour-hero-contain bg-light'
                                : ''
                        }`}
                    />
                )}
                <h4 id="onboarding-tour-title" className="fw-bold mb-3">
                    {current.title}
                </h4>
                <p className="text-muted mb-3">{current.body}</p>

                {current.link && (
                    <Button
                        as={Link}
                        to={current.link}
                        variant="outline-primary"
                        size="sm"
                        className="mb-3"
                        onClick={handleLinkClick}
                    >
                        {current.linkLabel}
                    </Button>
                )}

                <ProgressBar
                    now={progress}
                    className="mb-3 progress-extra-thin"
                    aria-label={`Tour progress: step ${step + 1} of ${TOUR_STEPS.length}`}
                />

                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted text-decoration-none"
                        onClick={handleSkip}
                    >
                        Skip tour
                    </Button>
                    <div className="d-flex gap-2">
                        {!isFirst && (
                            <Button
                                variant="outline-dark"
                                size="sm"
                                onClick={handlePrev}
                            >
                                Previous
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleNext}
                        >
                            {isLast ? 'Finish' : 'Next'}
                        </Button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

OnboardingTour.propTypes = {
    show: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    onPause: PropTypes.func,
    initialStep: PropTypes.number,
};
